import SearchIcon from '@mui/icons-material/Search';
import { IconButton, MenuItem, TextField } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSnackbar } from '@Providers/useSnackbar';
import usePlatformMode from '@Hooks/usePlatformMode';
import {
	CourseOption,
	InstructorOption,
	ProductOption,
	ProductType,
	ReviewState,
	SubmissionsFilters as Filters,
	UnitOption,
	fetchCourses,
	fetchProducts,
	fetchUnits,
} from '../Services/submissions.req';

type Props = {
	kind: 'test' | 'assignment';
	value: Filters;
	onChange: (next: Filters) => void;
	/** Admin only — the instructor app pins the owner server-side. */
	showInstructor: boolean;
};

const STATUS_OPTIONS: Array<{ value: ReviewState; label: string }> = [
	{ value: 'pending', label: 'Pending' },
	{ value: 'graded', label: 'Graded / closed' },
	{ value: 'all', label: 'All' },
];

/**
 * The cascade, top to bottom. Changing a level resets every level below it.
 */
const LEVELS = ['instructor_id', 'product_type', 'product_id', 'course_id', 'unit_id'] as const;
type Level = typeof LEVELS[number];

const resetBelow = (value: Filters, level: Level): Filters => {
	const next: Filters = { ...value };
	LEVELS.slice(LEVELS.indexOf(level) + 1).forEach((key) => {
		delete next[key];
	});
	return next;
};

const numberOrUndefined = (raw: string) => (raw === '' ? undefined : Number(raw));

/**
 * Cascading filter bar: Instructor → Product type → Product → Course/Lecture
 * → Unit, plus Status, date range and student search. Every level below a
 * changed one resets, and the page goes back to 1 through `onChange`.
 */
const SubmissionsFilters: React.FC<Props> = ({ kind, value, onChange, showInstructor }) => {
	const { displaySnackbar } = useSnackbar();
	const { classroomsEnabled, standaloneEnabled } = usePlatformMode();
	const [instructors, setInstructors] = useState<InstructorOption[]>([]);
	const [products, setProducts] = useState<ProductOption[]>([]);
	const [courses, setCourses] = useState<CourseOption[]>([]);
	const [units, setUnits] = useState<UnitOption[]>([]);
	const [searchInput, setSearchInput] = useState(value.query ?? '');

	// Only the families this platform runs (same rule as the scratch-card and
	// promo-code pickers); a single enabled family needs no chooser.
	const productTypes = useMemo(() => {
		const types: Array<{ value: ProductType; label: string }> = [];
		if (classroomsEnabled) types.push({ value: 'classroom', label: 'Classroom' });
		if (standaloneEnabled) types.push({ value: 'standalone', label: 'Standalone course' });
		return types;
	}, [classroomsEnabled, standaloneEnabled]);

	// With one family enabled the chooser above is not rendered, so nothing
	// could ever set product_type — the Product/Course/Unit cascade stayed
	// permanently disabled and, worse, kept the 'Classroom' label on a
	// standalone-only platform. Pin the single family instead.
	const soleType = productTypes.length === 1 ? productTypes[0].value : undefined;
	useEffect(() => {
		if (!soleType || value.product_type === soleType) return;
		onChange({ ...resetBelow(value, 'product_type'), product_type: soleType });
		// Settles in one pass: the guard above is false on the next render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [soleType, value.product_type]);

	// Level 1: instructors, once (admin only).
	useEffect(() => {
		if (!showInstructor) return;
		let cancelled = false;
		fetchProducts()
			.then((lists) => !cancelled && setInstructors(lists.instructors ?? []))
			.catch((error: any) => {
				displaySnackbar('error', error?.response?.data?.message ?? 'Unable to load instructors');
			});
		return () => {
			cancelled = true;
		};
	}, [showInstructor, displaySnackbar]);

	// Level 2: the chosen family's products, for the chosen instructor (or the
	// pinned owner). Nothing is fetched until a family is picked.
	useEffect(() => {
		if (!value.product_type) {
			setProducts([]);
			return;
		}
		let cancelled = false;
		fetchProducts(value.instructor_id, value.product_type)
			.then((lists) => {
				if (cancelled) return;
				setProducts((value.product_type === 'classroom' ? lists.classrooms : lists.standalone_courses) ?? []);
			})
			.catch((error: any) => {
				displaySnackbar('error', error?.response?.data?.message ?? 'Unable to load products');
			});
		return () => {
			cancelled = true;
		};
	}, [value.instructor_id, value.product_type, displaySnackbar]);

	// Level 3: courses / lectures of the chosen product.
	useEffect(() => {
		if (!value.product_type || !value.product_id) {
			setCourses([]);
			return;
		}
		let cancelled = false;
		fetchCourses(value.product_type, value.product_id)
			.then((rows) => !cancelled && setCourses(rows ?? []))
			.catch((error: any) => {
				displaySnackbar('error', error?.response?.data?.message ?? 'Unable to load courses');
			});
		return () => {
			cancelled = true;
		};
	}, [value.product_type, value.product_id, displaySnackbar]);

	// Level 4: units of the chosen course, of this tab's kind.
	useEffect(() => {
		if (!value.course_id) {
			setUnits([]);
			return;
		}
		let cancelled = false;
		fetchUnits(value.course_id, kind)
			.then((rows) => !cancelled && setUnits(rows ?? []))
			.catch((error: any) => {
				displaySnackbar('error', error?.response?.data?.message ?? 'Unable to load units');
			});
		return () => {
			cancelled = true;
		};
	}, [value.course_id, kind, displaySnackbar]);

	const applySearch = () => onChange({ ...value, query: searchInput.trim() });

	return (
		<Bar>
			{showInstructor && (
				<TextField
					select
					size="small"
					label="Instructor"
					value={value.instructor_id ?? ''}
					sx={{ minWidth: '12rem' }}
					onChange={(e) =>
						onChange({ ...resetBelow(value, 'instructor_id'), instructor_id: numberOrUndefined(e.target.value) })
					}
				>
					<MenuItem value="">All instructors</MenuItem>
					{instructors.map((instructor) => (
						<MenuItem key={instructor.id} value={instructor.id}>
							{instructor.first_name} {instructor.last_name}
						</MenuItem>
					))}
				</TextField>
			)}
			{productTypes.length > 1 && (
				<TextField
					select
					size="small"
					label="Product type"
					value={value.product_type ?? ''}
					sx={{ minWidth: '11rem' }}
					onChange={(e) =>
						onChange({
							...resetBelow(value, 'product_type'),
							product_type: (e.target.value || undefined) as ProductType | undefined,
						})
					}
				>
					<MenuItem value="">All types</MenuItem>
					{productTypes.map((type) => (
						<MenuItem key={type.value} value={type.value}>
							{type.label}
						</MenuItem>
					))}
				</TextField>
			)}
			<TextField
				select
				size="small"
				label={
					value.product_type === 'standalone'
						? 'Standalone course'
						: value.product_type === 'classroom'
						? 'Classroom'
						: 'Product'
				}
				value={value.product_id ?? ''}
				disabled={!value.product_type}
				sx={{ minWidth: '14rem' }}
				onChange={(e) => onChange({ ...resetBelow(value, 'product_id'), product_id: numberOrUndefined(e.target.value) })}
			>
				<MenuItem value="">All</MenuItem>
				{products.map((product) => (
					<MenuItem key={product.id} value={product.id}>
						{product.title}
						{product.label ? ` (${product.label})` : ''}
					</MenuItem>
				))}
			</TextField>
			<TextField
				select
				size="small"
				label={
					value.product_type === 'standalone'
						? 'Lecture'
						: value.product_type === 'classroom'
						? 'Course'
						: 'Course / Lecture'
				}
				value={value.course_id ?? ''}
				disabled={!value.product_id}
				sx={{ minWidth: '12rem' }}
				onChange={(e) => onChange({ ...resetBelow(value, 'course_id'), course_id: numberOrUndefined(e.target.value) })}
			>
				<MenuItem value="">All</MenuItem>
				{courses.map((course) => (
					<MenuItem key={course.id} value={course.id}>
						{course.name}
					</MenuItem>
				))}
			</TextField>
			<TextField
				select
				size="small"
				label="Unit"
				value={value.unit_id ?? ''}
				disabled={!value.course_id}
				sx={{ minWidth: '12rem' }}
				onChange={(e) => onChange({ ...value, unit_id: numberOrUndefined(e.target.value) })}
			>
				<MenuItem value="">All</MenuItem>
				{units.map((unit) => (
					<MenuItem key={unit.id} value={unit.id}>
						{unit.name}
					</MenuItem>
				))}
			</TextField>
			<TextField
				select
				size="small"
				label="Status"
				value={value.state}
				sx={{ minWidth: '10rem' }}
				onChange={(e) => onChange({ ...value, state: e.target.value as ReviewState })}
			>
				{STATUS_OPTIONS.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{option.label}
					</MenuItem>
				))}
			</TextField>
			<TextField
				size="small"
				type="date"
				label="From"
				InputLabelProps={{ shrink: true }}
				value={value.from ?? ''}
				onChange={(e) => onChange({ ...value, from: e.target.value || undefined })}
			/>
			<TextField
				size="small"
				type="date"
				label="To"
				InputLabelProps={{ shrink: true }}
				value={value.to ?? ''}
				onChange={(e) => onChange({ ...value, to: e.target.value || undefined })}
			/>
			<Search>
				<TextField
					size="small"
					placeholder="Search students"
					value={searchInput}
					onChange={(e) => setSearchInput(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') applySearch();
					}}
				/>
				<IconButton onClick={applySearch} aria-label="search">
					<SearchIcon />
				</IconButton>
			</Search>
		</Bar>
	);
};

const Bar = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	align-items: center;
	margin-bottom: 1.5rem;
`;

const Search = styled.div`
	display: flex;
	align-items: center;
	margin-left: auto;
`;

export default SubmissionsFilters;
